import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  CssBaseline,
  Divider,
  Grid,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Pagination,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddLocationAltIcon from "@mui/icons-material/AddLocationAlt";
import EditLocationAltIcon from "@mui/icons-material/EditLocationAlt";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PolylineIcon from "@mui/icons-material/Polyline";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";

import ZoneReadonlyMap from "../components/ZoneReadonlyMap";
import { getZoneListUseCase } from "../domain/usecases/getZoneListUseCase";
import Topbar from "../../../components/topbar/TopBar";

const PAGE_SIZE = 10;

function getZonePolygon(zone) {
  return Array.isArray(zone?.poligone) ? zone.poligone : zone?.polygon || [];
}

function getZoneCells(zone) {
  return Array.isArray(zone?.cells) ? zone.cells : [];
}

function getZoneLocation(zone) {
  return [
    zone.countryName || zone.country,
    zone.departmentName || zone.department,
    zone.provinceName || zone.province,
  ]
    .filter(Boolean)
    .join(" / ");
}

export default function ZoneListPage() {
  const navigate = useNavigate();
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const filteredZones = useMemo(() => {
    const value = search.trim().toLowerCase();

    if (!value) {
      return zones;
    }

    return zones.filter((zone) =>
      [
        zone.name,
        zone.description,
        zone.countryName,
        zone.departmentName,
        zone.provinceName,
        zone.country,
        zone.department,
        zone.province,
      ]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(value)),
    );
  }, [search, zones]);

  const totalVertices = useMemo(
    () =>
      filteredZones.reduce(
        (total, zone) => total + getZonePolygon(zone).length,
        0,
      ),
    [filteredZones],
  );

  const pageCount = Math.max(1, Math.ceil(filteredZones.length / PAGE_SIZE));

  const paginatedZones = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredZones.slice(start, start + PAGE_SIZE);
  }, [filteredZones, page]);

  const loadZones = async () => {
    try {
      setLoading(true);
      setError("");

      const zoneList = await getZoneListUseCase();
      setZones(zoneList);
      setSelectedZone(null);
    } catch (requestError) {
      setError(requestError.message || "No se pudieron cargar las zonas.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadZones();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    if (page > pageCount) {
      setPage(pageCount);
    }
  }, [page, pageCount]);

  useEffect(() => {
    if (!filteredZones.length) {
      setSelectedZone(null);
      return;
    }

    const selectedExists = filteredZones.some(
      (zone) => zone.id === selectedZone?.id,
    );

    if (!selectedExists) {
      setSelectedZone(null);
    }
  }, [filteredZones, selectedZone?.id]);

  return (
    <>
      <CssBaseline />
      <main className="content">
        <Topbar />
        <Box
          sx={{
            minHeight: "100vh",
            "& .MuiTypography-body2": {
              fontSize: "1rem",
            },
            "& .MuiTypography-caption": {
              fontSize: "0.875rem",
            },
            "& .MuiButton-root": {
              fontSize: "1rem",
            },
            "& .MuiChip-label": {
              fontSize: "0.9375rem",
            },
            "& .MuiInputBase-input": {
              fontSize: "1rem",
            },
            p: 1,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
            spacing={2}
            sx={{ mb: 1 }}
          >
            <Box>
              <Typography variant="h4" fontWeight={800}>
                Zonas geoespaciales
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                Visualiza los polígonos guardados por país, departamento y
                provincia.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.5}>
              <Button
                variant="outlined"
                color="success"
                startIcon={<RefreshIcon />}
                onClick={loadZones}
                disabled={loading}
              >
                Actualizar
              </Button>
              <Button
                variant="contained"
                startIcon={<AddLocationAltIcon />}
                onClick={() => navigate("/admin/zona-especial")}
              >
                Crear zona
              </Button>
            </Stack>
          </Stack>

          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Grid container spacing={2}>
            <Grid item xs={12} md={4} lg={3}>
              <Paper
                elevation={2}
                sx={{
                  borderRadius: 3,
                  overflow: "hidden",
                }}
              >
                <Box sx={{ p: 2.5 }}>
                  <TextField
                    fullWidth
                    placeholder="Buscar zona"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <Divider />

                <Stack direction="row" spacing={1} sx={{ p: 2.5 }}>
                  <Chip
                    icon={<LocationOnIcon />}
                    label={`${filteredZones.length} zonas`}
                    color="primary"
                    variant="outlined"
                  />
                  <Chip
                    icon={<PolylineIcon />}
                    label={`${totalVertices} vértices`}
                    variant="outlined"
                  />
                </Stack>

                <Divider />

                <Box
                  sx={{
                    maxHeight: { xs: 360, md: "calc(100vh - 310px)" },
                    overflow: "auto",
                  }}
                >
                  {loading ? (
                    <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
                      <CircularProgress size={28} />
                      <Typography variant="body2" color="text.secondary">
                        Cargando zonas...
                      </Typography>
                    </Stack>
                  ) : null}

                  {!loading && filteredZones.length === 0 ? (
                    <Box sx={{ p: 3 }}>
                      <Typography fontWeight={700}>Sin zonas</Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        mt={0.5}
                      >
                        No hay polígonos que coincidan con la búsqueda.
                      </Typography>
                    </Box>
                  ) : null}

                  {!loading && filteredZones.length > 0 ? (
                    <List disablePadding>
                      {paginatedZones.map((zone) => {
                        const polygon = getZonePolygon(zone);
                        const cells = getZoneCells(zone);

                        return (
                          <ListItemButton
                            key={zone.id}
                            selected={zone.id === selectedZone?.id}
                            onClick={() => setSelectedZone(zone)}
                            sx={{
                              alignItems: "flex-start",
                              px: 2.5,
                              py: 1.75,
                            }}
                          >
                            <ListItemText
                              primary={
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  spacing={1}
                                >
                                  <Typography fontWeight={700}>
                                    {zone.name || "Zona sin nombre"}
                                  </Typography>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<EditLocationAltIcon />}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      navigate(
                                        `/admin/zona-especial/actualizar/${zone.id}`,
                                      );
                                    }}
                                    sx={{
                                      color: "#fff",
                                      flexShrink: 0,
                                      "& .MuiButton-startIcon": {
                                        color: "#fff",
                                      },
                                      "& .MuiSvgIcon-root": {
                                        color: "#fff",
                                      },
                                    }}
                                  >
                                    Editar
                                  </Button>
                                </Stack>
                              }
                              secondary={
                                <Box component="span">
                                  <Typography
                                    component="span"
                                    variant="body2"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    {getZoneLocation(zone) ||
                                      "Ubicación no registrada"}
                                  </Typography>
                                  <Typography
                                    component="span"
                                    variant="body2"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    Multiplicador: {zone.multiplier ?? "1.00"} ·
                                    Resolución: {zone.resolution ?? "-"}
                                  </Typography>
                                  <Typography
                                    component="span"
                                    variant="body2"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    Especial: {zone.isSpecial ? "Sí" : "No"}
                                  </Typography>
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    {polygon.length} vértices · {cells.length}{" "}
                                    celdas
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  ) : null}
                </Box>

                {!loading && filteredZones.length > PAGE_SIZE ? (
                  <>
                    <Divider />
                    <Stack alignItems="center" sx={{ p: 2 }}>
                      <Pagination
                        count={pageCount}
                        page={page}
                        onChange={(event, value) => setPage(value)}
                        color="primary"
                        size="small"
                        siblingCount={0}
                        boundaryCount={1}
                      />
                    </Stack>
                  </>
                ) : null}
              </Paper>
            </Grid>

            <Grid item xs={12} md={8} lg={9}>
              <Paper
                elevation={2}
                sx={{
                  position: "relative",
                  height: {
                    xs: 460,
                    sm: 540,
                    md: "calc(100vh - 154px)",
                  },
                  minHeight: 460,
                  borderRadius: 3,
                  overflow: "hidden",
                }}
              >
                <ZoneReadonlyMap
                  zones={filteredZones}
                  selectedZoneId={selectedZone?.id}
                  onSelectZone={setSelectedZone}
                />

                {selectedZone ? (
                  <Paper
                    elevation={4}
                    sx={{
                      position: "absolute",
                      left: 16,
                      right: { xs: 16, md: "auto" },
                      bottom: 16,
                      width: { xs: "auto", md: 420 },
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    <Stack spacing={1}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={2}
                      >
                        <Typography variant="h6" fontWeight={800}>
                          {selectedZone.name || "Zona sin nombre"}
                        </Typography>
                        <Chip
                          size="small"
                          label={`${getZonePolygon(selectedZone).length} puntos`}
                        />
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {getZoneLocation(selectedZone) ||
                          "Ubicación no registrada"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Multiplicador: {selectedZone.multiplier ?? "1.00"} ·
                        Resolución: {selectedZone.resolution ?? "-"} · Especial:{" "}
                        {selectedZone.isSpecial ? "Sí" : "No"}
                      </Typography>
                      {selectedZone.description ? (
                        <Typography variant="body2">
                          {selectedZone.description}
                        </Typography>
                      ) : null}
                    </Stack>
                  </Paper>
                ) : null}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </main>
    </>
  );
}
